/**
 * Data Management feature hooks — centralized import/export/backup.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { dataManagementApi } from './api'

// ── Import / Export (existing) ───────────────────────────────────────────────

/** Import data for a specific entity (or 'all') */
export function useEntityImport(entity: string) {
    return useMutation({
        mutationFn: (files: File[]) => {
            const form = new FormData()
            files.forEach(f => form.append('files', f))
            return dataManagementApi.importEntity(entity, form)
        },
    })
}

/** Returns a function to trigger an authenticated file download for a specific entity */
export function useEntityExport() {
    return async (entity: string, format: 'json' | 'csv' = 'json') => {
        const response = await dataManagementApi.exportEntity(entity, format)

        // response.data is already a Blob when responseType is 'blob' — use it directly
        const blob: Blob = response.data
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url

        // Try extracting filename from Content-Disposition header
        const disposition = response.headers['content-disposition']
        const filenameMatch = disposition?.match(/filename="?(.+?)"?$/i)

        if (filenameMatch?.[1]) {
            link.download = filenameMatch[1]
        } else {
            // Fallback: detect ZIP by content-type or multi-entity request
            const contentType = blob.type || response.headers['content-type'] || ''
            const isZip = contentType.includes('zip') || entity.includes(',') || entity === 'all'
            const stamp = new Date().toISOString().replace(/[-:T]/g, '').slice(0, 15)
            link.download = isZip ? `itm_export_${stamp}.zip` : `${entity}_${stamp}.${format}`
        }

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
    }
}

// Legacy aliases for backwards compatibility with Data Management page
export const useSystemImport = () => useEntityImport('all')
export const useSystemExport = useEntityExport

/** Factory reset — wipe data by category (or all if empty) */
export function useFactoryReset() {
    return useMutation({
        mutationFn: (categories?: string[]) => dataManagementApi.factoryReset(categories),
    })
}

// ── Backup & Restore ─────────────────────────────────────────────────────────

export interface BackupItem {
    id: string
    name: string
    description: string | null
    entities: string[]
    storage_key: string
    storage_backend: string
    size_bytes: number
    created_at: string
    created_by: string | null
}

export interface TemplateItem {
    filename: string
    name: string
    description: string
    tags: string[]
    size_bytes: number
}

export interface StorageConfig {
    backend: string
    status: 'connected' | 'error' | 'unknown'
    error_message?: string
    max_count: number
    max_age_days: number
    path?: string
    endpoint?: string
    bucket?: string
    container?: string
    readonly_fields: string[]
    total_size_bytes: number
    used_size_bytes: number
    backups_size_bytes: number
    attachments_size_bytes: number
    templates_size_bytes: number
    free_size_bytes: number
}

/** Fetch all backups — query key: ['backups'] */
export function useBackups() {
    return useQuery<BackupItem[]>({
        queryKey: ['backups'],
        queryFn: async () => {
            const res = await dataManagementApi.listBackups()
            return res.data
        },
    })
}

/** Create a new backup */
export function useCreateBackup() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (payload: { name: string; description?: string; entities?: string[] }) =>
            dataManagementApi.createBackup(payload),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['backups'] })
        },
    })
}

/** Download a backup */
export function useDownloadBackup() {
    return async (backup: BackupItem) => {
        const response = await dataManagementApi.downloadBackup(backup.id)
        const blob: Blob = response.data
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${backup.name.replace(/\s+/g, '_')}.zip`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
    }
}

/** Delete a backup */
export function useDeleteBackup() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => dataManagementApi.deleteBackup(id),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['backups'] })
        },
    })
}

/** Restore from a backup */
export function useRestoreBackup() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => dataManagementApi.restoreBackup(id),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['backups'] })
        },
    })
}

// ── Templates ────────────────────────────────────────────────────────────────

/** Fetch built-in templates */
export function useTemplates() {
    return useQuery<TemplateItem[]>({
        queryKey: ['backup-templates'],
        queryFn: async () => {
            const res = await dataManagementApi.listTemplates()
            return res.data
        },
        staleTime: 5 * 60 * 1000,
    })
}

/** Import a built-in template */
export function useImportTemplate() {
    return useMutation({
        mutationFn: (filename: string) => dataManagementApi.importTemplate(filename),
    })
}

// ── Storage config ───────────────────────────────────────────────────────────

/** Fetch storage config */
export function useStorageConfig() {
    return useQuery<StorageConfig>({
        queryKey: ['storage-config'],
        queryFn: async () => {
            const res = await dataManagementApi.getStorageConfig()
            return res.data
        },
        staleTime: 30 * 1000,
    })
}

/** Update retention policy */
export function useUpdateRetentionPolicy() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (payload: { max_count: number; max_age_days: number }) =>
            dataManagementApi.updateRetentionPolicy(payload),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['storage-config'] })
        },
    })
}
